<template>
    <div
        ref="refAutoComplete"
        class="hts-form-autocomplete"
        :class="cClass">
        <label :for="name">
            {{ props.label }}<span
                v-if="required"
                class="text-error-500 ml-1">
                *
            </span>
        </label>
        <div class="hts-form-autocomplete__container">
            <span class="prefix">
                <slot name="prefix" />
            </span>
            <input
                :id="name"
                ref="input"
                v-model="search"
                type="text"
                :name="prop"
                :placeholder="placeholder"
                autocomplete="off"
                :disabled="disabled"
                @input="onInput"
                @keydown.down="onArrowDown"
                @keydown.up="onArrowUp"
                @keydown.enter="onEnter"
                @click="handleInputClick" >
            <span class="suffix">
                <slot name="suffix" />
            </span>
        </div>
        <div
            v-if="errorMessage"
            class="hts-form-autocomplete__error">
            {{ errorMessage }}
        </div>
        <hui-transition-root
            :show="isOpen"
            class="z-3"
            as="template">
            <hui-transition-child
                as="template"
                enter="hts-transition__enter-active"
                enter-from="hts-transition__enter-from"
                enter-to="hts-transition__enter-to"
                leave="hts-transition__leave-active"
                leave-from="hts-transition__leave-from"
                leave-to="hts-transition__leave-to">
                <ul
                    v-show="searched"
                    class="autocomplete-results">
                    <li
                        v-show="isInputing"
                        class="status">
                        <p>
                            {{ $t('labels.inputing') }}
                        </p>
                    </li>
                    <li
                        v-show="!results.length"
                        class="autocomplete-result">
                        <div class="item">
                            <div
                                v-if="!props.useText"
                                class="label notFound">
                                <p>
                                    {{ $t('labels.notFound') }}
                                </p>
                            </div>
                            <div
                                v-else
                                class="label"
                                @click="useTextAsResult()">
                                <p>
                                    {{ $t('labels.addNew') }}: "{{ search }}"
                                </p>
                            </div>
                        </div>
                    </li>
                    <li
                        v-for="(result, i) in results"
                        :key="i"
                        class="autocomplete-result"
                        :class="arrowCounter === i && 'active'"
                        @click="setResult(result)">
                        <div class="item">
                            <div
                                v-if="props.keyImage"
                                class="image">
                                <img :src="result[props.keyImage] ? result[props.keyImage] : defaultAvatarUrl" >
                            </div>
                            <div class="label">
                                <p>
                                    {{ result[props.keyLabel] }}
                                </p>
                            </div>
                            <div
                                v-if="props.keySub"
                                class="label sub">
                                <p>
                                    {{ result[props.keySub] }}
                                </p>
                            </div>
                        </div>
                    </li>
                    <li
                        v-show="props.add && checkPermission(props.permissionsAdd)"
                        class="autocomplete-result">
                        <div
                            class="item"
                            @click="handleClickAdd">
                            <p class="flex items-center">
                                <hi-plus-icon class="icon w-5 h-5" />
                                {{ $t('labels.addNew') }}
                            </p>
                        </div>
                    </li>
                </ul>
            </hui-transition-child>
        </hui-transition-root>
    </div>
</template>

<script setup lang="ts">
import { useField, useFieldError } from 'vee-validate'
import colorSize from '~/components/color-size'
import { checkPermission } from '@/utils/permission-check'

const { $searchObjects } = useNuxtApp()

const defaultAvatarUrl = ref('/assets/default-avatar.png')
const props = defineProps({
  // url will be replaced "KEY" to search
  server: {
    type: Object
  },
  items: {
    type: Array<{ (key: string): any }>
  },
  modelValue: {
    type: String,
    default: ''
  },
  isAsync: {
    type: Boolean,
    required: false,
    default: false
  },
  label: String,
  text: String,
  keyId: {
    type: String,
    required: false,
    default: 'code'
  },
  keyLabel: {
    type: String,
    required: false,
    default: 'name'
  },
  keyImage: {
    type: String,
    required: false,
    default: ''
  },
  keySub: {
    type: String,
    required: false,
    default: ''
  },
  keyType: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false
  },
  readonly: {
    type: Boolean,
    required: false,
    default: false
  },
  prop: {
    type: String,
    default: ''
  },
  name: String,
  placeholder: String,
  rules: {
    type: Array,
    default: () => []
  },
  color: {
    type: String,
    default: 'default'
  },
  size: {
    type: String,
    default: 'default'
  },
  required: Boolean,
  preload: {
    type: Boolean,
    default: true
  },
  add: {
    type: Boolean,
    c: true
  },
  permissionsAdd: {
    type: Array<string>,
    default: () => {
      return [] as string[]
    }
  },
  useText: {
    // sử dụng đoạn text đang search làm kết quá
    type: Boolean,
    default: false
  },
  vehicleLoad: {
    type: Number,
    default: 0
  },
  companyType: {
    type: String,
    default: ''
  },
  nodeType: {
    type: String,
    default: ''
  },
  remoocLength: {
    type: Number,
    default: 0
  }
})
const { name, prop } = toRefs(props)
let field: any
let errorMessage: undefined | ComputedRef
const cProp = computed(() => prop?.value)
const setFields = (prop: any) => {
  if (typeof prop === 'string' && prop !== '') {
    field = useField(prop, undefined, {
      label: name?.value || prop,
      validateOnValueUpdate: true,
      validateOnMount: false
    })
    errorMessage = useFieldError(cProp.value)
  }
}
watch(
  () => cProp.value,
  (val: any) => {
    setFields(val)
  }
)
onMounted(() => {
  setFields(cProp.value)
})
const cValue = computed({
  get: () => {
    if (props.modelValue) {
      field?.setValue(props.modelValue)
    }
    return props.modelValue
  },
  set: (val: any) => {
    if (!$lodash.isNil(val)) {
      field?.setValue($lodash.get(val, props.keyId, ''))
    }
    emits('update:modelValue', val)
  }
})

const emits = defineEmits(['update:modelValue', 'change', 'add'])
const cbModelValue = !props.disabled && !props.readonly && useVModel(props, 'modelValue', emits)

const refInput = templateRef<HTMLInputElement | null>('input', null)

const { cClass } = colorSize(props)

const search = ref('')
const isOpen = ref(false)
const arrowCounter = ref(-1)
const isLoading = ref(false)
const isInputing = ref(false)
const searched = ref(true)
const refAutoComplete = ref(null)
const results = ref<any[]>([])

onClickOutside(refAutoComplete, () => {
  isOpen.value = false
})

const handleInputClick = () => {
  isOpen.value = true
}
const onInput = () => {
  isOpen.value = true
  isInputing.value = true
  inputing()
  searching()
}

const inputing = $lodash.debounce(() => {
  isInputing.value = false
}, 450)

const searching = $lodash.debounce(() => {
  if (props.items) {
    isLoading.value = false
    results.value = $searchObjects(props.items, props.keyLabel, refInput?.value?.value || '', { i: true })
    arrowCounter.value = -1
    searched.value = true
    return
  }
  const server = { ...props.server }
  const val = refInput?.value?.value
  const uriParams = {
    KEY: val || '',
    vehicleLoadData: props.vehicleLoad || '',
    TYPE: props.companyType.toUpperCase() || '',
    nodeType: props.nodeType.toUpperCase() || '',
    LENGTH: props.remoocLength || 0
  }
  isLoading.value = true
  $api(server, { uriParams })
    .then((data) => {
      const resData: any = data?.data.value

      if (resData.success && Array.isArray(resData.data)) {
        isLoading.value = false
        results.value = resData.data
        arrowCounter.value = -1
        searched.value = true
      }
    })
    .catch(() => {
      isLoading.value = false
      results.value = []
      searched.value = true
    })
}, 500)

const setResult = (result: { (key: string): any }) => {
  if (!result || typeof result !== 'object') {
    return
  }
  if (!props.keyId || !(props.keyId in result)) {
    return
  }
  if (!props.keyLabel || !(props.keyLabel in result)) {
    return
  }

  search.value = result[props.keyLabel]
  cValue.value = result
  emits('update:modelValue', result[props.keyId])
  const change = !props.keyType
    ? {
      [props.keyId]: result[props.keyId],
      [props.keyLabel]: result[props.keyLabel]
    }
    : {
      [props.keyId]: result[props.keyId],
      [props.keyLabel]: result[props.keyLabel],
      [props.keyType]: result[props.keyType]
    }
  emits('change', change, result)
  isOpen.value = false
}

const onArrowDown = (event: Event) => {
  event.stopPropagation()
  if (arrowCounter.value < results.value.length - 1) {
    arrowCounter.value = arrowCounter.value + 1
  }
}

const onArrowUp = (event: Event) => {
  event.stopPropagation()
  if (arrowCounter.value > 0) {
    arrowCounter.value = arrowCounter.value - 1
  }
}

const onEnter = (event: Event) => {
  event.preventDefault()

  if (!results.value || !Array.isArray(results.value)) {
    return
  }
  if (!arrowCounter.value && arrowCounter.value !== 0) {
    return
  }
  if (arrowCounter.value < 0 || arrowCounter.value >= results.value.length) {
    return
  }

  setResult(results.value[arrowCounter.value])
  arrowCounter.value = -1
}

const useTextAsResult = () => {
  const data: any = {
    [props.keyId]: search.value
  }

  if (props.keyId !== props.keyLabel) {
    data[props.keyLabel] = search.value
  }
  setResult({ ...data })
  // results.value = $searchObjects(props.items, props.keyLabel, '', { i: true })
}

const handleClickAdd = (event: Event) => {
  emits('add')
}

const _init = (): void => {
  const text = String(props.text)

  if (text === 'deleteResults') {
    search.value = ''
  } else if (text && text !== 'undefined') {
    search.value = text
    if (props.modelValue !== null) {
      field?.setValue(props.modelValue)
    }

    searched.value = false
  } else {
    props.preload && searching()
  }
}

_init()

watch(props, (oldData, newData) => {
  _init()
})
</script>
